import { useEffect, useRef, useState } from 'react'
import { AppState, Platform } from 'react-native'
import { useAtomValue } from 'jotai'
import { usePostHog } from 'posthog-react-native'
import * as Sentry from '@sentry/react-native'

import { auth } from './Firebase/firebase'
import { userAtom } from './AtomsProvider'
import appJson from '../app.json'
import { getInstallId } from '../utils/installIdentity'
import { readInstallSecret, runAppleAdsAttribution } from '../utils/appleAdsAttribution'
import { captureAppFirstOpened, linkInstallToOrganization, readInstallIdentitySecret, registerInstallIdentity } from '../utils/growthMilestones'
import { postWebAcquisition, resolveWebAcquisition } from '../utils/webAcquisition'

const API_BASE_URL = process.env.EXPO_PUBLIC_BACKEND_API_URL || null

/**
 * ASO-ATTR-001 — the React wiring for Apple Ads attribution and the device-observed milestone.
 *
 * WHERE IT IS MOUNTED AND WHY IT MATTERS. Inside `PostHogProvider` in Routes.js, beside
 * `AnalyticsIdentityBridge`, and therefore BELOW the `if (isLoading) return <Spinner/>` early
 * return. That placement is the definition of "after the first ready screen": this component
 * cannot mount until Routes has decided which stack to render, so `app_first_opened` cannot fire
 * on a launch that never got past the spinner. It is above the AppStack/AuthStack branch for the
 * same reason the identity bridge is — an Apple Ads install is measured from BEFORE signup, so
 * anything living in AppStack would miss the entire population the campaign is judged on.
 *
 * ALL OF THE DECISION-MAKING LIVES IN utils/. This file is lifecycle, platform gates and effect
 * injection only, so launch / offline / refusal / account-switch are assertable without mounting a
 * component wired to Firebase, PostHog and a native module — the same split, for the same reason,
 * as `createAuthIdentityHandler`.
 *
 * WHY AN AppState LISTENER AND NOT ONLY A MOUNT EFFECT. A till is a device that is opened once and
 * then left running for days. A mount-only attempt would give an install that launched with no
 * network exactly one chance, ever, for the life of that JS process. Foreground transitions are
 * the retry cadence that matches how the device is actually used — bounded by the state machine's
 * own `MIN_RETRY_INTERVAL_MS`, so a user flipping between apps cannot turn it into a request loop.
 *
 * WHAT LOGOUT DOES: NOTHING, DELIBERATELY. The install id is a property of the INSTALLATION, not
 * of a person, so a sign-out must not clear it — clearing it would mint a second install id for
 * one install and split that install's attribution from its own signup. The install→organization
 * link is keyed by the pair, so signing into a second organization links that pair too, which is
 * the truth on a shared till. Nothing here is reset on `postHog.reset()` either: this bridge
 * registers no super properties, precisely so it has nothing to restore on that edge (ASO-AN-001's
 * "a restore-on-teardown is only sound while the teardown it pairs with is the only one").
 */
export default function GrowthAttributionBridge() {
  const postHog = usePostHog()
  const user = useAtomValue(userAtom)
  const organizationId = user?.organizationId || null

  // One attribution run at a time per process. The AppState listener and the mount effect can
  // otherwise overlap on a cold foreground, and two concurrent runs read the same pre-attempt
  // record and would both attempt.
  const attributionInFlight = useRef(false)

  // Whether this INSTALLATION existed before the install id did, decided ONCE at mount.
  //
  // ⚠️ READ AT MOUNT, NOT PER RUN, AND THAT DISTINCTION IS THE WHOLE CORRECTNESS ARGUMENT. The
  // AppState listener re-runs `runAttribution` on every foreground, and re-reading `auth.currentUser`
  // there would consult a value that MOVES: a genuinely new install whose first capture neither
  // fired nor latched (AsyncStorage threw, or PostHog was not ready) and whose user then signed up
  // would, on the next foreground, read a signed-in user and be suppressed forever — the gate
  // deleting the exact population it exists to measure. At mount the answer is stable and correct
  // for the launch that matters: Routes.js clears `isLoading` only inside its `onAuthStateChanged`
  // callback, so Firebase has already reported a state by the time this component renders, and a
  // fresh install reports null.
  //
  // `useRef` with a lazy-looking initialiser is deliberate over `useState`: this is read by an
  // effect, never rendered, and must not be able to trigger a re-render that re-enters the effect.
  const priorInstallEvidence = useRef(Boolean(auth.currentUser))

  // Bumped after every attribution run so the LINK effect below re-reads the install secret.
  //
  // The two effects are independent, and on a SIGNUP session they resolve in the wrong order: the
  // organization appears while attribution is still in flight, so the link effect reads the secret
  // before there is one and gives up with `no-install-secret`. Keyed only on `organizationId` it
  // would not run again — self-healing on the next launch, which is fine for a phone and not fine
  // for a till that stays open for days. This makes "attribution finished" an input to the link.
  const [attributionRuns, setAttributionRuns] = useState(0)

  // The organization whose web-acquisition link is being posted right now, or null.
  //
  // ⚠️ THE EFFECT BELOW RE-FIRES ON EVERY FOREGROUND, AND ITS WORK IS NOT INSTANT. `postWebAcquisition`
  // reads its latch, then awaits an auth token and a network round trip before writing it — so two
  // runs that overlap both read "not linked yet" and both POST. That is the same overlap
  // `attributionInFlight` exists to prevent for the Apple Ads run above, and it became reachable
  // here the moment this effect stopped being once-per-process. Keyed on the ORGANIZATION rather
  // than a bare boolean: an account switch mid-flight is a different link and must never be dropped.
  const webAcquisitionInFlight = useRef(null)

  // ── app_first_opened + Apple Ads attribution ────────────────────────────────────────────────
  useEffect(() => {
    if (!postHog) return
    let cancelled = false

    const onError = (error) => {
      console.warn('[growth] attribution bridge step failed; app continues', error)
      Sentry.captureException(error)
    }

    const runAttribution = async () => {
      if (attributionInFlight.current) return
      attributionInFlight.current = true
      try {
        const installId = await getInstallId()
        if (cancelled) return

        // This handshake is intentionally before and independent of AdServices. Organic installs,
        // unsupported binaries and unresolved tokens all acquire the same identity chain.
        if (API_BASE_URL) {
          await registerInstallIdentity({
            postHog,
            installId,
            apiBaseUrl: API_BASE_URL,
          })
        }
        if (cancelled) return

        // Fired for EVERY platform, not only iOS: it is the product's first-open milestone, and an
        // Apple-only version of it would have no denominator to compare an Apple Ads cohort with.
        //
        // ⚠️ THE RELEASE GATE, AND WHY THE EVIDENCE IS READ *HERE* RATHER THAN INSIDE THE UTILITY.
        // The latch is keyed by install id, and the install id is minted the first time
        // `getInstallId()` is called — which for every copy of the app already on a device is the
        // first launch after this ships. Ungated, the whole installed base emits `app_first_opened`
        // inside the OTA window and the first-open funnel takes a spike of "installs" that are
        // months old. `GROWTH_MILESTONE_FLOOR` is the backend's answer to the same hazard; this is
        // the device's, and it has to live at the call site because the evidence is Firebase auth —
        // which `utils/growthMilestones.js` deliberately does not import, so its decisions stay
        // assertable without mounting anything.
        //
        // The value itself is decided ONCE, at mount — see `priorInstallEvidence` above for why
        // re-reading it per foreground would suppress the very installs this is measuring. It is
        // sound at that moment, and only because of where this component is mounted: Routes.js
        // clears `isLoading` exclusively inside its `onAuthStateChanged` callback, so this bridge
        // cannot render until Firebase has reported a state at least once. Anywhere above that
        // spinner it would read null on a signed-in till and emit the event it is here to suppress.
        await captureAppFirstOpened({
          postHog,
          installId,
          properties: { platform: Platform.OS },
          priorInstallEvidence: priorInstallEvidence.current,
        })
        if (cancelled) return

        if (!API_BASE_URL) return
        await runAppleAdsAttribution({
          installId,
          apiBaseUrl: API_BASE_URL,
          appVersion: appJson.expo.version,
          onError,
        })
      } catch (error) {
        onError(error)
      } finally {
        attributionInFlight.current = false
        // After the run, not before: the link effect's job is to use whatever the run persisted.
        if (!cancelled) setAttributionRuns((n) => n + 1)
      }
    }

    void runAttribution()

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') void runAttribution()
    })

    return () => {
      cancelled = true
      // RN >= 0.65 returns a subscription; the older global-listener API is long gone from this
      // app's RN version, but a missing `remove` must not throw out of a cleanup function.
      subscription?.remove?.()
    }
  }, [postHog])

  // ── install → organization link ─────────────────────────────────────────────────────────────
  // Separate effect, keyed on the organization AND on `attributionRuns`, because it becomes possible
  // strictly later than everything above (auth → user document → organizationId), must re-run on an
  // account switch, and — on a signup session — must re-run once attribution has actually stored the
  // install secret it needs. Both latches downstream are idempotent, so a redundant run is a no-op.
  useEffect(() => {
    if (!organizationId || !API_BASE_URL) return
    let cancelled = false

    void (async () => {
      try {
        const installId = await getInstallId()
        // Issued once by the token endpoint, on this install's first submission; absent for any
        // install that never submitted one (Android, web, an iOS binary without the module), which
        // `linkInstallToOrganization` treats as "nothing to link" rather than as an error.
        const installSecret = (await readInstallIdentitySecret(installId)) || (await readInstallSecret(installId))
        if (cancelled) return
        await linkInstallToOrganization({
          installId,
          installSecret,
          organizationId,
          apiBaseUrl: API_BASE_URL,
          getAuthToken: () => auth.currentUser?.getIdToken() ?? Promise.resolve(null),
          onError: (error) => {
            console.warn('[growth] install→organization link failed; app continues', error)
            Sentry.captureException(error)
          },
        })
      } catch (error) {
        console.warn('[growth] install→organization link threw; app continues', error)
        Sentry.captureException(error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [organizationId, attributionRuns])

  // ── web acquisition → organization link (WEB-ATTR-2026-001) ────────────────────────────────
  //
  // Separate from the Apple Ads link above because it answers a different question for a different
  // population: that one links an INSTALL Apple attributed, this one links the marketing-site touch
  // a browser carried across `atlas-app.org` → `web.atlas-app.org`. A device has at most one of the
  // two, never both, so folding them into one effect would only make each one's failure mode
  // depend on the other's preconditions.
  //
  // `resolveWebAcquisition()` is called here as well as in `FunnelScreen` on purpose, and the
  // reason is the population it catches: a user who lands on `web.atlas-app.org?utm_source=…`,
  // does NOT sign up that session, and signs up later from a session with no parameters at all.
  // Only a resolve that runs on every load persists the touch in time for that. It is idempotent —
  // the stored first touch is write-once — so running it twice on a signup session costs nothing.
  //
  // ⚠️ KEYED ON `attributionRuns` TOO, exactly as the Apple Ads link above is. `postWebAcquisition`
  // budgets its retries (8 fast attempts, then once per 24h) on the assumption — stated in its own
  // docblock — that it "runs from an effect that re-fires on every foreground". Keyed on the
  // organization alone it fires ONCE per JS process, so a link that failed on a dead network at
  // signup would not be retried until the app was relaunched: on a till left running for days, the
  // acquisition of the organization that just signed up is simply lost. Both latches downstream are
  // idempotent, so the extra runs cost a storage read.
  useEffect(() => {
    let cancelled = false

    // ⚠️ THE RESOLVE IS NOT GATED ON AN ORGANIZATION, AND GATING IT DELETED THE POPULATION THE
    // COMMENT ABOVE SAYS THIS CALL EXISTS FOR. A visitor who lands on
    // `web.atlas-app.org?utm_source=…` — an ad pointing straight at the app, so there is no
    // marketing-site cookie anywhere — is signed OUT, so an effect that returned early on a missing
    // `organizationId` never persisted that touch. By the time they come back and sign up, the URL
    // carries nothing and there is no carrier to read: the click is gone. Only the POST needs a
    // tenant to attribute to; the resolve is a local AsyncStorage fold and must run on every load.
    //
    // The in-flight claim is keyed by organization, `''` standing for "signed out", so an account
    // switch mid-flight is still a different claim and is never dropped.
    const claim = organizationId ?? ''

    void (async () => {
      if (webAcquisitionInFlight.current === claim) return
      webAcquisitionInFlight.current = claim
      try {
        const state = await resolveWebAcquisition()
        // Null is the normal answer on native and for an organic direct signup. Not an error, not
        // latched, and deliberately not posted: a row asserting "no acquisition" would be
        // indistinguishable from one asserting "direct", and the two are different facts.
        if (cancelled || !state) return
        // Nothing to attribute it TO yet — the touch is persisted and the next run, once signup has
        // produced an organization, posts it.
        if (!organizationId || !API_BASE_URL) return
        await postWebAcquisition({
          state,
          organizationId,
          apiBaseUrl: API_BASE_URL,
          platform: Platform.OS,
          getAuthToken: () => auth.currentUser?.getIdToken() ?? Promise.resolve(null),
          onError: (error) => {
            console.warn('[growth] web acquisition link failed; app continues', error)
            Sentry.captureException(error)
          },
        })
      } catch (error) {
        console.warn('[growth] web acquisition link threw; app continues', error)
        Sentry.captureException(error)
      } finally {
        // Only clear our own claim: an account switch may already have replaced it.
        if (webAcquisitionInFlight.current === claim) webAcquisitionInFlight.current = null
      }
    })()

    return () => {
      cancelled = true
    }
  }, [organizationId, attributionRuns])

  return null
}
